import { newCompetitionId } from "../../domain/cmoe/competition/competitionTypes";
import { newMatchId } from "../../domain/cmoe/match/matchTypes";
import {
  roomMatchStatusToOrchestrationState,
  orchestrationStateToRoomMatchStatus,
} from "../../domain/cmoe/match/matchStateMachine";
import { leaseFromManagedBy, managedByFromLease } from "../../domain/cmoe/lease/managerLease";
import {
  getRoomSessionPolicyBundle,
  ROOM_SESSION_POLICY_BUNDLE_ID,
  ROOM_SESSION_POLICY_BUNDLE_VERSION,
} from "../../domain/cmoe/policy/roomSessionPolicyBundle";
import { resolveRoomGovernanceRole } from "../../domain/roomGovernance/roomGovernanceLogic";
import {
  roomOwnerCapabilities,
  roomDeputyCapabilities,
} from "../../domain/cmoe/permission/capabilityCatalog";

/**
 * @param {string} roomCode
 * @param {string} [competitionId]
 * @returns {import('../../domain/cmoe/competition/competitionTypes').CompetitionMeta}
 */
export function mapRoomToCompetitionMeta(roomCode, competitionId = newCompetitionId()) {
  return {
    competitionId,
    competitionKind: "room_session",
    policyBundleId: ROOM_SESSION_POLICY_BUNDLE_ID,
    policyBundleVersion: ROOM_SESSION_POLICY_BUNDLE_VERSION,
    status: "running",
    externalRefs: { roomCode },
    metadata: {},
  };
}

/**
 * @param {import('../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch} roomMatch
 * @param {string} competitionId
 * @param {Record<string, string>} [participantBindings] registryEntryId → participantId
 * @returns {import('../../domain/cmoe/match/matchTypes').MatchSummary}
 */
export function mapRoomMatchToSummary(roomMatch, competitionId, participantBindings = {}) {
  const bindings = { ...participantBindings };
  (roomMatch.participantEntryIds || []).forEach((entryId) => {
    if (entryId) bindings[`slot_${entryId}`] = entryId;
  });
  return {
    matchId: roomMatch.id,
    competitionId,
    orchestrationState: roomMatchStatusToOrchestrationState(roomMatch.status),
    orchestrationVersion: 1,
    displayOrdinal: roomMatch.sequenceNumber || 1,
    bindings,
    resourceBindings: roomMatch.courtNumber != null ? { court: String(roomMatch.courtNumber) } : {},
    activityModuleRef: getRoomSessionPolicyBundle().activity.defaultModule,
    managerLease: leaseFromManagedBy(roomMatch.managedBy, 1),
    consumerMetadata: {
      roomId: roomMatch.roomId,
      competitionId: roomMatch.competitionId,
    },
    historySyncStatus: "ok",
  };
}

/**
 * @param {import('../../domain/cmoe/match/matchTypes').MatchSummary} summary
 * @returns {Pick<import('../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch, 'status' | 'managedBy'>}
 */
export function mapSummaryToRoomMatchFields(summary) {
  return {
    status: orchestrationStateToRoomMatchStatus(summary.orchestrationState),
    managedBy: managedByFromLease(summary.managerLease) || summary.managerLease.holderUid || "",
  };
}

/**
 * @param {string | null | undefined} uid
 * @param {{ createdBy?: string, deputyRefereeUid?: string | null } | null} roomDoc
 * @returns {string[]}
 */
export function mapRoomRoleToCapabilities(uid, roomDoc) {
  const role = resolveRoomGovernanceRole(uid, roomDoc);
  if (role === "owner") return roomOwnerCapabilities();
  if (role === "deputy") return roomDeputyCapabilities();
  return ["match.view", "activity.view"];
}

/**
 * @param {import('../../domain/cmoe/match/matchTypes').MatchSummary} summary
 * @param {string} createdBy
 * @param {string} roomId
 * @returns {import('../../domain/roomGovernance/roomGovernanceTypes').RoomSessionMatch}
 */
export function mapSummaryToRoomSessionMatchStub(summary, createdBy, roomId) {
  const fields = mapSummaryToRoomMatchFields(summary);
  return {
    id: summary.matchId,
    roomId,
    competitionId: summary.consumerMetadata?.competitionId || null,
    courtNumber: summary.resourceBindings?.court ? Number(summary.resourceBindings.court) : null,
    status: fields.status,
    createdBy,
    managedBy: fields.managedBy || createdBy,
    lockedBy: null,
    lockedAt: null,
    participantEntryIds: Object.values(summary.bindings),
    sequenceNumber: summary.displayOrdinal,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Registry row → competition participant (room: participantId = registry entry id).
 * @param {import('../../features/registry/models/registryTypes').RegistryEntry | null | undefined} entry
 * @param {string} competitionId
 */
export function mapRegistryEntryToParticipant(entry, competitionId) {
  if (!entry?.id) return null;
  return {
    participantId: entry.id,
    competitionId,
    registryEntryId: entry.id,
    playerId: entry.playerId || null,
    displayName: entry.displayName || entry.name || "",
  };
}

/**
 * @param {{ createdBy?: string, deputyRefereeUid?: string | null }} roomDoc
 * @returns {{ uid: string, permissions: string[] }[]}
 */
export function buildCompetitionPermissionGrants(roomDoc) {
  const grants = [];
  if (roomDoc?.createdBy) {
    grants.push({ uid: roomDoc.createdBy, permissions: roomOwnerCapabilities() });
  }
  if (roomDoc?.deputyRefereeUid) {
    grants.push({
      uid: roomDoc.deputyRefereeUid,
      permissions: roomDeputyCapabilities(),
    });
  }
  return grants;
}

export { newMatchId };
